// Trip utility functions

import { Trip } from '../api/tripsApi';
import { formatMoney, parseAmount } from './money';

export const normalizeStatus = (status: string): string => {
  return status.toLowerCase().replace(/\s+/g, '_');
};

export const getStatusDisplay = (status: string): string => {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case 'draft':
      return '🚀 Draft';
    case 'in_progress':
      return '🚀 In Progress';
    case 'confirmed':
      return '✅ Confirmed';
    case 'planning':
      return '📋 Planning';
    case 'completed':
      return '✅ Completed';
    default:
      return status;
  }
};

/**
 * "CA$2,000" / "¥150,000" — the trip budget however the server shaped it, or
 * null when there is none to show.
 *
 * Through the shared money module rather than a hand-kept symbol table. That
 * table mapped JPY and CNY both to "¥", so two currencies 20x apart printed the
 * same; it rounded nothing, so a per-day figure came out "C$222.222"; and its
 * legacy branch stamped "$" onto a bare number that carries no currency at all.
 *
 * `whole: true` — a trip budget is a round figure someone chose. Activity costs
 * are real prices and keep their cents.
 */
export const formatBudget = (budget: number | string | { amount: number; currency: string } | undefined): string | null => {
  if (budget === undefined || budget === null || budget === '') {
    return null;
  }

  if (typeof budget === 'object' && budget !== null) {
    return formatMoney(budget, { whole: true }) || null;
  }

  // A bare number or numeric string: an amount with no currency attached, so it
  // is shown without one. Guessing "$" is the defect above.
  const amount = parseAmount(budget);
  if (amount === null) {
    return String(budget);
  }
  return formatMoney({ amount, currency: '' }, { whole: true });
};

export const formatDestinations = (destinations: string[] | undefined): string => {
  return destinations && destinations.length > 0 ? destinations.join(', ') : 'No destinations';
};

/** True when the record is a top-level trip, not an activity row from the trips index. */
export const isTripRecord = (trip: Trip): boolean => {
  const id = String(trip.id ?? '');
  if (!id || id.includes('#ACTIVITY#')) return false;
  if ((trip as Trip & { tripId?: string }).tripId) return false;
  return true;
};

/** Trips that have enough itinerary metadata to show in profile/trip lists. */
export const isSubstantiveTrip = (trip: Trip): boolean => {
  if (!isTripRecord(trip)) return false;
  return (
    (trip.destinations?.length ?? 0) > 0 ||
    Boolean(trip.dates?.startDate) ||
    Boolean(trip.conversation_id)
  );
};

export const filterTripRecords = (trips: Trip[]): Trip[] => trips.filter(isSubstantiveTrip);

export const getTripImage = (images: string[] | undefined): string => {
  return images && images.length > 0 ? images[0] : '✈️';
};

export const getFilteredTrips = (trips: Trip[]) => {
  const drafted = trips.filter(trip => normalizeStatus(trip.status) === 'draft');
  const inProgress = trips.filter(trip => normalizeStatus(trip.status) === 'in_progress');
  const upcoming = trips.filter(trip => {
    const normalized = normalizeStatus(trip.status);
    return normalized === 'confirmed' || normalized === 'planning';
  });
  const past = trips.filter(trip => normalizeStatus(trip.status) === 'completed');

  return { drafted, inProgress, upcoming, past };
};

